import type { HTMLAttributes } from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import type { LocaleKey } from "@/i18n";
import { getFieldCopy } from "@/i18n";
import type { CdcFieldCopy } from "./cdc-field-helpers";
import { EmailField } from "../email-field";

export interface CdcEmailFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends HTMLAttributes<HTMLDivElement> {
  control: Control<TFieldValues>;
  locale: LocaleKey;
  name: TName;
  isConfirmation?: boolean;
  disabled?: boolean;
  prefixWarning?: boolean;
  onPrefixWarningChange?: (hasWarning: boolean) => void;
}

export function CdcEmailField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  locale,
  name,
  isConfirmation = false,
  disabled = false,
  prefixWarning = false,
  onPrefixWarningChange,
  className,
  ...htmlProps
}: Readonly<CdcEmailFieldProps<TFieldValues, TName>>) {
  const copy = isConfirmation
    ? (getFieldCopy(locale, "confirmEmail") as CdcFieldCopy)
    : (getFieldCopy(locale, "email") as CdcFieldCopy);

  return (
    <EmailField
      control={control}
      name={name}
      label={copy.label}
      placeholder={copy.placeholder}
      description={copy.description}
      required
      requiredLabel={copy.errors?.required ?? "Required"}
      disabled={disabled}
      sharedMailboxWarning={prefixWarning}
      sharedMailboxWarningTitle={copy.prefixWarningTitle}
      sharedMailboxWarningDescription={copy.prefixWarningDescription}
      onSharedMailboxWarningChange={onPrefixWarningChange}
      className={className}
      {...htmlProps}
    />
  );
}
